import type {Dayjs} from "dayjs";
import type {Localization} from "@mui/material/locale";
import {csCZ, deDE, enUS, frFR, itIT, plPL} from "@mui/material/locale";
import type {PickersInputLocaleText} from "@mui/x-date-pickers/locales";
import {
    csCZ as pickersCsCZ,
    deDE as pickersDeDE,
    enUS as pickersEnUS,
    frFR as pickersFrFR,
    itIT as pickersItIT,
    plPL as pickersPlPL
} from "@mui/x-date-pickers/locales";
import {Language} from "@/api/data-contracts";
import {LANGUAGE_TO_LNG} from "@/app/locale/languageCodes";

// Side-effect imports registering the Day.js locales named in LANGUAGE_TO_DAYJS_LOCALE below. They
// live here, next to the map, so every caller of resolveDayjsLocale gets the locale registered by
// importing this module - inside the rendered LocaleProvider as well as outside it (lib/utils.ts,
// unit tests).
import "dayjs/locale/de";
import "dayjs/locale/en-gb";
import "dayjs/locale/pl";
import "dayjs/locale/it";
import "dayjs/locale/fr";
import "dayjs/locale/cs";

/**
 * Maps each supported `Language` to its MUI core localization object (component texts such as
 * `TablePagination` labels or the date picker toolbar).
 */
const LANGUAGE_TO_MUI_LOCALE: Record<Language, Localization> = {
    [Language.GERMAN]: deDE,
    [Language.ENGLISH]: enUS,
    [Language.POLISH]: plPL,
    [Language.ITALIAN]: itIT,
    [Language.FRENCH]: frFR,
    [Language.CZECH]: csCZ
};

/**
 * Reverse of `LANGUAGE_TO_LNG` (i18next code -> `Language` enum value), derived from the same
 * single source of truth instead of a second hardcoded language list.
 */
const LNG_TO_LANGUAGE: Record<string, Language> = Object.fromEntries(
    Object.entries(LANGUAGE_TO_LNG).map(([language, lng]) => [lng, language as Language])
);

/**
 * Shared resolution rule of this module: looks the i18next language code up in a per-`Language` map
 * and falls back to the German entry for an unknown or empty code. Holds the business rule "German
 * is the fallback of the presentation layer" in exactly one place for all resolvers below.
 */
function resolveForLng<T>(lng: string | undefined, map: Record<Language, T>): T {
    const language = lng ? LNG_TO_LANGUAGE[lng] : undefined;
    return language ? map[language] : map[Language.GERMAN];
}

/**
 * Resolves the MUI core locale object (`@mui/material/locale`) for the given i18next language code
 * (`i18n.language`, e.g. `"de"`/`"en"`). Defaults to the German locale (`deDE`) - the app's standard
 * language (i18n `lng: "de"`) - for an unknown or empty code.
 */
export function resolveMuiCoreLocale(lng: string | undefined): Localization {
    return resolveForLng(lng, LANGUAGE_TO_MUI_LOCALE);
}

/**
 * German locale text bundle shipped with `@mui/x-date-pickers` - placeholders (`TT`/`JJJJ`),
 * calendar navigation, view switching, clear/toolbar/action-bar labels.
 */
const PICKERS_LOCALE_TEXT_DE = pickersDeDE.components.MuiLocalizationProvider.defaultProps.localeText;

/**
 * English counterpart of `PICKERS_LOCALE_TEXT_DE`.
 */
const PICKERS_LOCALE_TEXT_EN = pickersEnUS.components.MuiLocalizationProvider.defaultProps.localeText;

/**
 * Polish counterpart of `PICKERS_LOCALE_TEXT_DE`.
 */
const PICKERS_LOCALE_TEXT_PL = pickersPlPL.components.MuiLocalizationProvider.defaultProps.localeText;

/**
 * Italian counterpart of `PICKERS_LOCALE_TEXT_DE`.
 */
const PICKERS_LOCALE_TEXT_IT = pickersItIT.components.MuiLocalizationProvider.defaultProps.localeText;

/**
 * French counterpart of `PICKERS_LOCALE_TEXT_DE`.
 */
const PICKERS_LOCALE_TEXT_FR = pickersFrFR.components.MuiLocalizationProvider.defaultProps.localeText;

/**
 * Czech counterpart of `PICKERS_LOCALE_TEXT_DE`.
 */
const PICKERS_LOCALE_TEXT_CS = pickersCsCZ.components.MuiLocalizationProvider.defaultProps.localeText;

/**
 * Maps each supported `Language` to the locale text bundle of the date picker library.
 */
const LANGUAGE_TO_PICKERS_LOCALE_TEXT: Record<Language, PickersInputLocaleText<Dayjs>> = {
    [Language.GERMAN]: PICKERS_LOCALE_TEXT_DE,
    [Language.ENGLISH]: PICKERS_LOCALE_TEXT_EN,
    [Language.POLISH]: PICKERS_LOCALE_TEXT_PL,
    [Language.ITALIAN]: PICKERS_LOCALE_TEXT_IT,
    [Language.FRENCH]: PICKERS_LOCALE_TEXT_FR,
    [Language.CZECH]: PICKERS_LOCALE_TEXT_CS
};

/**
 * Maps each supported `Language` to its Day.js locale name. Typing this as `Record<Language, string>`
 * makes the compiler demand an entry for every future language instead of silently falling back at
 * runtime.
 *
 * COUPLING (not enforced by the type): a locale name only takes effect once the matching Day.js
 * locale module is loaded. The static side-effect imports at the top of this file do that. A new
 * entry here therefore REQUIRES a matching `import "dayjs/locale/<name>"` up there - without it
 * `dayjs.locale(...)` silently keeps the previous locale.
 *
 * English resolves to `"en-gb"`, not `"en"`: the Day.js locale drives the date-picker pattern and its
 * placeholder, and the English display convention of this application is the British one
 * (`DD/MM/YYYY`, see ADR-015). Plain `"en"` carries the US order `MM/DD/YYYY` and would show a
 * different reading of the same date than the outgoing mail does.
 */
const LANGUAGE_TO_DAYJS_LOCALE: Record<Language, string> = {
    [Language.GERMAN]: "de",
    [Language.ENGLISH]: "en-gb",
    [Language.POLISH]: "pl",
    [Language.ITALIAN]: "it",
    [Language.FRENCH]: "fr",
    [Language.CZECH]: "cs"
};

/**
 * Resolves the locale text bundle of `@mui/x-date-pickers` for the given i18next language code
 * (`i18n.language`, e.g. `"de"`/`"en"`). Defaults to the German bundle for an unknown or empty
 * code - German is the fallback of the presentation layer.
 */
export function resolveMuiPickersLocaleText(lng: string | undefined): PickersInputLocaleText<Dayjs> {
    return resolveForLng(lng, LANGUAGE_TO_PICKERS_LOCALE_TEXT);
}

/**
 * Resolves the Day.js locale name for the given i18next language code (`i18n.language`,
 * e.g. `"de"`/`"en"`). Defaults to `"de"` for an unknown or empty code.
 */
export function resolveDayjsLocale(lng: string | undefined): string {
    return resolveForLng(lng, LANGUAGE_TO_DAYJS_LOCALE);
}
